/**
 * Cuánto suman los impuestos de pedido —el IVA que se suma, el IGTF— con unos
 * pagos dados.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *  ⚠️ ESTA CUENTA TIENE QUE DAR EXACTAMENTE LO MISMO QUE EL SERVIDOR
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Está escrita dos veces a propósito —aquí y en `_impuestos_de_pedido` de
 * `pos/service.py`— porque el cajero tiene que ver el total ANTES de mandar
 * nada. Si las dos no coinciden, la venta se rechaza con «sobran 0,90» delante
 * del cliente y nadie entiende por qué.
 *
 * Vivía dentro de `ModalPago`, sin ninguna prueba. Salió aquí cuando llegaron
 * los productos exentos, que la hicieron más delicada: ahora las pruebas fijan
 * los mismos casos que `backend/tests/test_exentos.py`.
 *
 * Las reglas, palabra por palabra, son las de allá:
 *
 *   · La base es lo que ENTRA en las monedas de ese impuesto, convertido a la
 *     moneda de referencia. El IGTF grava el pago en divisas, no la compra.
 *   · El vuelto NO se resta: ver el comentario largo del servidor.
 *   · Se topa a lo debido: pagar con un billete de 200 una compra de 100 no
 *     paga el impuesto sobre 200.
 *   · Un impuesto sin monedas alcanza a la venta entera, **y esos van
 *     PRIMERO**: el IGTF grava lo que el cliente entrega, que ya lleva el IVA.
 *   · Lo exento sale de la base de cada uno: ver `lib/exentos.ts`.
 */

import { sinLoExento } from "@/lib/exentos";
import type { Tax } from "@/lib/types";

/** A las 4 decimales del servidor. Es `redondearMonto` del carrito. */
function q(n: number): number {
  return Math.round(n * 10000) / 10000;
}

export function recargoDeImpuestosDePedido(o: {
  /** Los que el cajero encendió en esta venta. */
  encendidos: Pick<Tax, "id" | "rate" | "currency_codes">[];
  /** Lo que se debe antes de estos impuestos, ya con los descuentos. */
  total: number;
  /** Lo que entró en cada moneda, ya en la moneda de referencia. */
  entradoPorMoneda: Record<string, number>;
  /** Ver `TotalesCarrito`. Sin ellos, nada está exento. */
  exentoPorImpuesto?: Record<string, number>;
  sumaDeLineas?: number;
}): number {
  const { encendidos, total: base, entradoPorMoneda } = o;
  if (encendidos.length === 0 || base <= 0) return 0;

  const exento = o.exentoPorImpuesto ?? {};
  const sumaDeLineas = o.sumaDeLineas ?? base;

  // ── Pasada 1: los que alcanzan a la venta entera (el IVA que se suma) ────
  let sinMoneda = 0;
  for (const t of encendidos) {
    if ((t.currency_codes ?? []).length > 0) continue;
    const suBase = sinLoExento(base, exento[t.id] ?? 0, sumaDeLineas);
    sinMoneda += q(suBase * (Number(t.rate) || 0));
  }

  // Lo que el cliente debe ya con esos dentro. Es el tope de los siguientes.
  const debido = q(base + sinMoneda);

  // ── Pasada 2: los que solo alcanzan a ciertas monedas (el IGTF) ──────────
  let conMoneda = 0;
  for (const t of encendidos) {
    const alcanzadas = t.currency_codes ?? [];
    if (alcanzadas.length === 0) continue;
    const entrado = alcanzadas.reduce((acc, c) => acc + (entradoPorMoneda[c] ?? 0), 0);
    const gravable = sinLoExento(
      Math.min(Math.max(entrado, 0), debido),
      exento[t.id] ?? 0,
      sumaDeLineas,
    );
    conMoneda += q(gravable * (Number(t.rate) || 0));
  }

  return q(sinMoneda + conMoneda);
}
